import { useState } from "react";
import { AxiosError } from "axios";
import accountService from "../accountService";
import { createSearchParams, useNavigate } from "react-router-dom";
import { baseRoutes } from "@base/routes/baseRoutes";
import authService from "@base/modules/auth/authService";
import { LocalKey } from "@base/constants/LocalKey";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { getErrorMsg } from "@base/helpers/common";

const useOTP = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | any>(null);

  const handleCheckOTP = async (phone_number: string, otp: number, isResetPassword: boolean = false) => {
    try {
      setLoading(true);
      await accountService.checkOTP(phone_number, otp);

      // Check OTP success, do login
      const data = await authService.login(phone_number, String(otp));

      // Only persist the token to localStorage so the setup-password screen can call
      // authenticated APIs. Do NOT dispatch to redux yet - that would make App.tsx
      // treat the user as fully logged in (fetch user info / stringee token) and
      // bounce them away from the setup-password screen before they finish.
      if (isResetPassword) {
        localStorage.setItem(
          LocalKey.AUTH,
          JSON.stringify({
            access_token: data?.data?.access_token,
            refresh_token: data?.data?.refresh_token,
          }),
        );
      } else {
        localStorage.setItem(LocalKey.AUTH, JSON.stringify(data?.data));
      }

      navigate({
        pathname: baseRoutes.login,
        search: createSearchParams({
          setupPassword: "true",
          phone: phone_number,
          otp: String(otp),
          reset: isResetPassword ? "true" : "false",
        }).toString(),
      });
    } catch (error: AxiosError | any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  /** Resolves to whether an OTP went out, so callers only move on once it really has. */
  const handleGetOTP = async (phone_number: string, lang: string, turnstileToken?: string) => {
    try {
      setLoading(true);
      await accountService.getOTP(phone_number, lang, turnstileToken);

      return true;
    } catch (error: AxiosError | any) {
      setError(error);

      const status = error?.response?.status;
      const description =
        status === 403
          ? t("LoginPage.AuthenticationRetry")
          : status === 429
            ? t("LoginPage.OtpLimitExceeded")
            : (error?.response?.data?.description ?? getErrorMsg(error));

      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description }));

      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isLoading,
    error,
    handleCheckOTP,
    handleGetOTP,
  };
};

export default useOTP;
